/**
 * Motor del Mapa_Operacional: LayerManager, ClusterEngine y selección.
 *
 * Implementa la parte de **interacción** del Mapa_Operacional (R1) sobre
 * las Capa_Mapa derivadas por `./mapLayers` y el Grafo_Entidades de
 * `./graph`:
 *
 *   - **LayerManager** (`toggleCapa` / {@link LayerManager}): gestiona el
 *     conjunto de Capa_Mapa activas/inactivas. Activar o desactivar una
 *     capa afecta **únicamente** a esa Capa_Mapa y **conserva** el estado
 *     de las demás (R1.2). El núcleo es una función **pura** que recibe el
 *     set de capas activas y devuelve el nuevo set, sin mutar la entrada.
 *
 *   - **ClusterEngine** (`clusterFeatures` / {@link ClusterEngine}):
 *     agrupa los elementos de una Capa_Mapa con Supercluster cuando su
 *     densidad supera un umbral configurable (default 200), exponiendo el
 *     **conteo exacto** por cluster (R1.3), y los desagrupa (declustering)
 *     al bajar del umbral o al aumentar el zoom (R1.4). El conteo total se
 *     **conserva** entre agrupar y desagrupar (Property 1).
 *
 *   - **Selección** (`detalleDeElemento` / {@link MapEngine.select}):
 *     dado un elemento del mapa, devuelve un {@link DetalleEntidad} con su
 *     id, tipo, estado vigente y timestamp del último Evento_Operacional,
 *     más los enlaces a las entidades relacionadas según el
 *     Grafo_Entidades (R1.6).
 *
 * Principios de diseño:
 *   - **Funciones puras y testeables en Node** (`toggleCapa`,
 *     `clusterFeatures`, `detalleDeElemento`): no hacen `fetch` ni tocan
 *     estado global; reciben sus dependencias por parámetro. Son el
 *     sustrato de las Properties 1, 2 y 3 (los tests se implementan en una
 *     tarea posterior).
 *   - **No duplicación (R17)**: las entidades se referencian por
 *     `entidad_ref` y `node_id` deterministas, reutilizando los de
 *     `./graph` y `./mapLayers`; no se redefine el esquema de la fundación.
 *
 * La terminología (Mapa_Operacional, Capa_Mapa, Cluster, DetalleEntidad,
 * Grafo_Entidades, Evento_Operacional) corresponde al Glosario de
 * `requirements.md`.
 *
 * _Requirements: 1.2, 1.3, 1.4, 1.6_
 */

import Supercluster from "supercluster";
import type { BBox } from "geojson";

import type {
  CapaMapa,
  Coordenada,
  DetalleEntidad,
  EntidadRef,
  EventStore,
  EventoOperacional,
  Graph,
  Id,
  InstantePlayback,
  IsoTimestamp,
  RangoTemporal,
  Result,
  Subgrafo,
} from "../api/contracts";
import type { ColeccionCapa, FeatureMapa, FeaturePropsMapa } from "./mapLayers";

// ---------------------------------------------------------------------------
// LayerManager — gestión pura del estado de Capa_Mapa activas (R1.2)
// ---------------------------------------------------------------------------

/**
 * Todas las Capa_Mapa renderizables sobre el Mapa_Operacional (R1.1),
 * en orden de capa. Versión en tiempo de ejecución del tipo `CapaMapa`
 * de `../api/contracts`.
 */
export const CAPAS_MAPA: readonly CapaMapa[] = [
  "dmas",
  "valvulas",
  "prvs",
  "cuadrillas",
  "presion",
  "cobertura_rf",
  "gateways",
  "ordenes",
  "anomalias",
  "heatmap_nrw",
  "fugas_historicas",
  "simulacion",
];

/**
 * Conjunto de Capa_Mapa activas por defecto: todas las capas de estado
 * real (R1.1), excluyendo la capa `simulacion`, que solo se activa al
 * ejecutar una Simulacion_Hidraulica o un Aislamiento_Valvular (R2.5).
 */
export function capasPorDefecto(): Set<CapaMapa> {
  return new Set(CAPAS_MAPA.filter((c) => c !== "simulacion"));
}

/**
 * Conmuta el estado activo/inactivo de **una** Capa_Mapa y devuelve el
 * **nuevo** conjunto de capas activas, conservando intacto el estado de
 * las demás Capa_Mapa (R1.2). Función **pura**: no muta el conjunto de
 * entrada, lo que la hace directamente testeable (Property 2 —
 * independencia de las Capa_Mapa ante toggles).
 *
 * @param activas Conjunto actual de Capa_Mapa activas (no se muta).
 * @param capa    La Capa_Mapa a activar o desactivar.
 * @returns       Un nuevo conjunto con `capa` conmutada.
 */
export function toggleCapa(activas: ReadonlySet<CapaMapa>, capa: CapaMapa): Set<CapaMapa> {
  const siguiente = new Set<CapaMapa>(activas);
  if (siguiente.has(capa)) {
    siguiente.delete(capa);
  } else {
    siguiente.add(capa);
  }
  return siguiente;
}

/** Indica si una Capa_Mapa está activa en el conjunto dado. */
export function isCapaActiva(activas: ReadonlySet<CapaMapa>, capa: CapaMapa): boolean {
  return activas.has(capa);
}

/**
 * LayerManager: contenedor con estado del conjunto de Capa_Mapa activas
 * del Mapa_Operacional. Toda mutación delega en la función pura
 * {@link toggleCapa}, de modo que activar/desactivar una capa afecte
 * solo a esa capa y conserve las demás (R1.2).
 */
export class LayerManager {
  private activas: Set<CapaMapa>;

  constructor(iniciales: ReadonlySet<CapaMapa> = capasPorDefecto()) {
    this.activas = new Set(iniciales);
  }

  /** Muestra/oculta únicamente esa Capa_Mapa, conservando las demás (R1.2). */
  toggleLayer(capa: CapaMapa): void {
    this.activas = toggleCapa(this.activas, capa);
  }

  /** Indica si una Capa_Mapa está activa. */
  isActiva(capa: CapaMapa): boolean {
    return isCapaActiva(this.activas, capa);
  }

  /** Devuelve una copia inmutable del conjunto de Capa_Mapa activas. */
  capasActivas(): ReadonlySet<CapaMapa> {
    return new Set(this.activas);
  }
}

// ---------------------------------------------------------------------------
// ClusterEngine — agrupación/desagrupación con conteo exacto (R1.3, R1.4)
// ---------------------------------------------------------------------------

/** Umbral de densidad por defecto a partir del cual se agrupa (R1.3). */
export const UMBRAL_CLUSTER_DEFAULT = 200;

/** Cluster de elementos con conteo exacto, alineado a {@link Cluster}. */
export interface ClusterMapa {
  coord: Coordenada;
  /** Conteo exacto de elementos agrupados en el cluster (R1.3). */
  count: number;
  /** Ids de los elementos individuales cuando el cluster se desagrupa (R1.4). */
  elementos?: Id[];
}

/** Límites de longitud/latitud admitidos por la proyección Web Mercator. */
const LAT_MAX_MERCATOR = 85.0511287798;

function clamp(valor: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, valor));
}

/**
 * Bounding box global del mundo en coordenadas Web Mercator
 * `[oeste, sur, este, norte]`. Se usa **siempre** como ventana de consulta
 * de `Supercluster.getClusters`, de modo que la consulta por tiles cubra la
 * **totalidad** de los elementos a cualquier nivel de zoom, preservando el
 * conteo total con independencia de la distribución de los puntos —incluidos
 * datasets vacíos o con puntos co-localizados o casi co-localizados, cuyo
 * bbox ajustado tendría área ~nula y devolvería 0 clusters, violando la
 * conservación (R1.3/R1.4, Property 1).
 */
const BBOX_MUNDIAL: BBox = [-180, -LAT_MAX_MERCATOR, 180, LAT_MAX_MERCATOR];

/** Normaliza un nivel de zoom arbitrario al rango entero soportado [0, 20]. */
function normalizarZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return 0;
  return clamp(Math.round(zoom), 0, 20);
}

/** Proyecta una Feature individual como un cluster unitario (count = 1). */
function clusterIndividual(f: FeatureMapa): ClusterMapa {
  const [lon, lat] = f.geometry.coordinates;
  return { coord: { lon, lat }, count: 1, elementos: [f.properties.id] };
}

/**
 * Agrupa los elementos de una Capa_Mapa en clusters según su densidad y
 * el nivel de zoom (R1.3, R1.4). Función **pura**: dada una
 * `FeatureCollection` y un zoom, devuelve los clusters con conteo
 * **exacto**, sin tocar estado global. Es el sustrato de la Property 1
 * (conservación de elementos en clustering).
 *
 * Reglas:
 *   - Si la cantidad de elementos es menor o igual al `umbral`, **no** se
 *     agrupa: cada elemento se devuelve como un cluster individual
 *     (count = 1) con su id (declustering por densidad, R1.4).
 *   - Si supera el `umbral`, se usa Supercluster al nivel de zoom dado;
 *     al aumentar el zoom, Supercluster desagrupa progresivamente los
 *     clusters en sus elementos individuales (declustering por zoom,
 *     R1.4).
 *
 * Invariante (Property 1): la suma de `count` sobre todos los clusters
 * devueltos es igual al número de elementos de entrada.
 *
 * @param coleccion FeatureCollection de la Capa_Mapa (geometrías Point).
 * @param zoom      Nivel de zoom actual del Mapa_Operacional.
 * @param umbral    Umbral de densidad configurable (rango 10–5000; default 200).
 */
export function clusterFeatures(
  coleccion: ColeccionCapa,
  zoom: number,
  umbral: number = UMBRAL_CLUSTER_DEFAULT,
): ClusterMapa[] {
  const features = coleccion?.features ?? [];
  if (features.length === 0) return [];

  // Densidad en o por debajo del umbral → sin agrupar (declustering, R1.4).
  if (features.length <= umbral) {
    return features.map(clusterIndividual);
  }

  const index = new Supercluster<FeaturePropsMapa>({
    radius: 40,
    minPoints: 2,
    minZoom: 0,
    maxZoom: 20,
  });
  index.load(features);

  const clusters = index.getClusters(BBOX_MUNDIAL, normalizarZoom(zoom));
  return clusters.map((f): ClusterMapa => {
    const [lon, lat] = f.geometry.coordinates as [number, number];
    const props = f.properties;
    if (props && (props as { cluster?: boolean }).cluster) {
      // Cluster agregado: el conteo exacto lo aporta Supercluster (R1.3).
      return { coord: { lon, lat }, count: (props as { point_count: number }).point_count };
    }
    // Elemento individual desagrupado (R1.4).
    return { coord: { lon, lat }, count: 1, elementos: [(props as FeaturePropsMapa).id] };
  });
}

/**
 * ClusterEngine: envoltura con configuración del umbral de densidad
 * sobre {@link clusterFeatures}. Mantiene la firma del diseño
 * `Map.cluster(elementos, zoom, umbral=200)`.
 */
export class ClusterEngine {
  constructor(private readonly umbral: number = UMBRAL_CLUSTER_DEFAULT) {}

  /** Agrupa/desagrupa con conteo exacto al nivel de zoom dado (R1.3, R1.4). */
  cluster(coleccion: ColeccionCapa, zoom: number, umbral: number = this.umbral): ClusterMapa[] {
    return clusterFeatures(coleccion, zoom, umbral);
  }
}

// ---------------------------------------------------------------------------
// Selección — DetalleEntidad consistente con el Grafo_Entidades (R1.6)
// ---------------------------------------------------------------------------

/**
 * Elemento del Mapa_Operacional susceptible de selección. Las
 * propiedades de una `FeatureMapa` ({@link FeaturePropsMapa}) satisfacen
 * esta forma, de modo que el resultado de `./mapLayers` se pueda
 * seleccionar directamente.
 */
export interface ElementoSeleccionable {
  /** `node_id` determinista en el Grafo_Entidades (consistencia R1.6). */
  node_id: Id;
  /** Referencia lógica a la entidad subyacente (no la duplica, R17). */
  entidad_ref: EntidadRef;
  /** Estado vigente legible, si lo trae la Feature. */
  estado?: string;
}

/** Estado mostrado cuando un elemento carece de estado vigente conocido. */
const ESTADO_DESCONOCIDO = "desconocido";

/**
 * Construye el {@link DetalleEntidad} de un elemento seleccionado a
 * partir de su subgrafo de vecinos y del timestamp de su último
 * Evento_Operacional (R1.6). Función **pura**: dado el mismo elemento,
 * subgrafo y timestamp, produce siempre el mismo detalle, en orden
 * determinista. Es el sustrato de la Property 3 (detalle de selección
 * consistente con el Grafo).
 *
 * Los `relacionados` son las entidades vecinas según el Grafo_Entidades,
 * excluyendo el propio elemento, ordenadas de forma determinista por
 * `node_id`. El `estado` proviene del elemento o, en su defecto, de los
 * atributos del nodo en el subgrafo.
 *
 * @param elemento         Elemento seleccionado (id, tipo, estado).
 * @param vecinos          Subgrafo de vecinos del elemento (R11.4).
 * @param ultimo_evento_ts Timestamp del último Evento_Operacional (R1.6).
 */
export function detalleDeElemento(
  elemento: ElementoSeleccionable,
  vecinos: Subgrafo,
  ultimo_evento_ts: IsoTimestamp,
): DetalleEntidad {
  const propio = vecinos.nodos.find((n) => n.node_id === elemento.node_id);

  const relacionados = vecinos.nodos
    .filter((n) => n.node_id !== elemento.node_id)
    .slice()
    .sort((a, b) => (a.node_id < b.node_id ? -1 : a.node_id > b.node_id ? 1 : 0))
    .map((n) => n.entidad_ref);

  const estado =
    elemento.estado ??
    (propio?.attrs?.["estado"] as string | undefined) ??
    ESTADO_DESCONOCIDO;

  return {
    id: elemento.entidad_ref.id,
    tipo: elemento.entidad_ref.tipo,
    estado,
    ultimo_evento_ts,
    relacionados,
  };
}

// ---------------------------------------------------------------------------
// MapEngine — fachada que compone LayerManager, ClusterEngine y selección
// ---------------------------------------------------------------------------

/**
 * Dependencias del {@link MapEngine}: el Grafo_Entidades (para los
 * vecinos de la selección, R1.6) y, opcionalmente, un resolutor del
 * timestamp del último Evento_Operacional de una entidad. Recibir el
 * grafo y el event store como dependencias mantiene el motor testeable y
 * desacoplado de los almacenes concretos.
 */
export interface MapEngineDeps {
  /** Grafo_Entidades para resolver los vecinos del elemento (R1.6, R11.4). */
  graph: Graph;
  /** Profundidad de vecindad para los `relacionados` de la selección (default 1). */
  profundidadVecinos?: number;
  /**
   * Resuelve el ts del último Evento_Operacional de una entidad (R1.6).
   * Si no se provee, el motor recurre al atributo `ultimo_evento_ts`
   * proyectado en el nodo del Grafo por el Event_Store.
   */
  ultimoEventoTs?: (
    ref: EntidadRef,
  ) => Promise<IsoTimestamp | null> | IsoTimestamp | null;
}

/**
 * MapEngine: motor de interacción del Mapa_Operacional que compone el
 * {@link LayerManager} (R1.2), el {@link ClusterEngine} (R1.3, R1.4) y la
 * selección de elementos (R1.6), exponiendo la API conceptual del diseño
 * (`toggleLayer`, `cluster`, `select`).
 */
export class MapEngine {
  private readonly layers: LayerManager;
  private readonly clusterer: ClusterEngine;

  constructor(
    private readonly deps: MapEngineDeps,
    opciones: { capasIniciales?: ReadonlySet<CapaMapa>; umbral?: number } = {},
  ) {
    this.layers = new LayerManager(opciones.capasIniciales ?? capasPorDefecto());
    this.clusterer = new ClusterEngine(opciones.umbral ?? UMBRAL_CLUSTER_DEFAULT);
  }

  /** Muestra/oculta solo esa Capa_Mapa, conservando las demás (R1.2). */
  toggleLayer(capa: CapaMapa): void {
    this.layers.toggleLayer(capa);
  }

  /** Conjunto vigente de Capa_Mapa activas. */
  capasActivas(): ReadonlySet<CapaMapa> {
    return this.layers.capasActivas();
  }

  /** Indica si una Capa_Mapa está activa. */
  isCapaActiva(capa: CapaMapa): boolean {
    return this.layers.isActiva(capa);
  }

  /** Agrupa/desagrupa con conteo exacto al nivel de zoom dado (R1.3, R1.4). */
  cluster(coleccion: ColeccionCapa, zoom: number, umbral?: number): ClusterMapa[] {
    return umbral === undefined
      ? this.clusterer.cluster(coleccion, zoom)
      : this.clusterer.cluster(coleccion, zoom, umbral);
  }

  /**
   * Selecciona un elemento del Mapa_Operacional y devuelve su
   * {@link DetalleEntidad}: id, tipo, estado vigente y timestamp del
   * último Evento_Operacional, más los enlaces a las entidades
   * relacionadas según el Grafo_Entidades (R1.6).
   */
  async select(elemento: ElementoSeleccionable): Promise<Result<DetalleEntidad>> {
    const profundidad = this.deps.profundidadVecinos ?? 1;
    const vecinosRes = await this.deps.graph.neighbors(elemento.node_id, profundidad);
    if (!vecinosRes.ok) {
      return { ok: false, error: vecinosRes.error };
    }

    let ts: IsoTimestamp | null = null;
    if (this.deps.ultimoEventoTs) {
      ts = await this.deps.ultimoEventoTs(elemento.entidad_ref);
    }
    if (!ts) {
      const propio = vecinosRes.value.nodos.find((n) => n.node_id === elemento.node_id);
      ts = (propio?.attrs?.["ultimo_evento_ts"] as IsoTimestamp | undefined) ?? "";
    }

    return { ok: true, value: detalleDeElemento(elemento, vecinosRes.value, ts) };
  }
}

/** Tipo de utilidad re-exportado para callers que tipan eventos en `select`. */
export type { EventoOperacional };

// ---------------------------------------------------------------------------
// PlaybackController — Playback_Temporal derivado del Event_Store (R2.1, R2.2, R2.7)
// ---------------------------------------------------------------------------

/**
 * Comando de control del Playback_Temporal (R2.2):
 *   - `play` / `pause`: alternan la reproducción sin mover el instante vigente;
 *   - `next` / `prev`: avanzan/retroceden un instante, **sin exceder** los
 *     límites `[inicio, fin]` del rango;
 *   - `seek`: salta a un índice arbitrario, que se **acota** al rango de
 *     instantes disponibles.
 *
 * Es la entrada del reductor puro {@link avanzarInstante}, sustrato de la
 * Property 6 (invariante de límites del Playback_Temporal).
 */
export type ComandoPlayback =
  | { tipo: "play" }
  | { tipo: "pause" }
  | { tipo: "next" }
  | { tipo: "prev" }
  | { tipo: "seek"; indice: number };

/**
 * Estado del Playback_Temporal: la secuencia de {@link InstantePlayback}
 * derivada del Event_Store dentro del rango, el índice del instante
 * vigente y si la reproducción está activa. El `indice` se mantiene
 * **siempre** dentro de `[0, instantes.length - 1]` (o 0 si no hay
 * instantes), de modo que el instante vigente nunca exceda los límites
 * del rango (R2.2).
 */
export interface EstadoPlayback {
  /** Instantes reproducibles, ordenados por ts no decreciente, dentro del rango. */
  readonly instantes: readonly InstantePlayback[];
  /** Índice del instante vigente, acotado a `[0, instantes.length - 1]`. */
  readonly indice: number;
  /** Si la reproducción está activa (no afecta los límites del instante). */
  readonly reproduciendo: boolean;
}

/** Clave estable de una entidad para agrupar su estado durante el playback. */
function entityKeyPlayback(ref: EntidadRef): string {
  return `${ref.tipo}:${ref.id}`;
}

/** Parsea un instante ISO-8601 a epoch ms; `NaN` si no es parseable. */
function parseTs(ts: IsoTimestamp): number {
  return Date.parse(ts);
}

/**
 * Indica si un {@link RangoTemporal} es válido: su instante inicial es
 * **estrictamente anterior** al final (R2.7). Rangos con instantes no
 * parseables o con `inicio ≥ fin` son inválidos.
 */
export function rangoValido(rango: RangoTemporal): boolean {
  const i = parseTs(rango.inicio);
  const f = parseTs(rango.fin);
  if (Number.isFinite(i) && Number.isFinite(f)) return i < f;
  // Fallback a comparación lexicográfica de ISO-8601 (ordenable como texto).
  return rango.inicio < rango.fin;
}

/** Indica si un instante `ts` cae dentro de `[inicio, fin]` inclusive. */
function tsEnRango(ts: IsoTimestamp, rango: RangoTemporal): boolean {
  const t = parseTs(ts);
  const i = parseTs(rango.inicio);
  const f = parseTs(rango.fin);
  if (Number.isFinite(t) && Number.isFinite(i) && Number.isFinite(f)) {
    return t >= i && t <= f;
  }
  return ts >= rango.inicio && ts <= rango.fin;
}

/** Ordena eventos por ts no decreciente, desempatando por `seq` (R9.4). */
function compararEventoTemporal(a: EventoOperacional, b: EventoOperacional): number {
  const ta = parseTs(a.ts);
  const tb = parseTs(b.ts);
  if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
  if (a.ts !== b.ts) return a.ts < b.ts ? -1 : 1;
  return a.seq - b.seq;
}

/**
 * Deriva la secuencia de {@link InstantePlayback} del Playback_Temporal a
 * partir de un log de Eventos_Operacionales y un rango temporal (R2.1).
 *
 * Función **pura y determinista**: filtra los eventos cuyo `ts` cae dentro
 * de `[inicio, fin]`, los ordena por ts no decreciente (desempatando por
 * `seq`), y para cada instante distinto produce el **estado acumulado** de
 * cada entidad reproducido hasta ese instante (fold incremental de los
 * payloads, en línea con el replay determinista del Event_Store, R9.4).
 *
 * El resultado está **acotado al rango**: todo `InstantePlayback.ts`
 * pertenece a `[inicio, fin]`. Es el sustrato de la Property 6 junto con
 * {@link avanzarInstante}.
 *
 * @param eventos Log de Eventos_Operacionales (cualquier orden de llegada).
 * @param rango   Rango temporal `[inicio, fin]` del Playback_Temporal.
 */
export function instantesEnRango(
  eventos: readonly EventoOperacional[],
  rango: RangoTemporal,
): InstantePlayback[] {
  const relevantes = eventos
    .filter((ev) => tsEnRango(ev.ts, rango))
    .slice()
    .sort(compararEventoTemporal);

  // Estado acumulado por entidad (fold incremental de payloads).
  const acumulado = new Map<string, { entidad_ref: EntidadRef; estado: Record<string, unknown> }>();
  const instantes: InstantePlayback[] = [];
  let tsActual: IsoTimestamp | null = null;

  const snapshot = (ts: IsoTimestamp): InstantePlayback => ({
    ts,
    estados: [...acumulado.values()]
      .slice()
      .sort((a, b) =>
        entityKeyPlayback(a.entidad_ref) < entityKeyPlayback(b.entidad_ref) ? -1 : 1,
      )
      .map((e) => ({ entidad_ref: e.entidad_ref, estado: { ...e.estado } })),
  });

  for (const ev of relevantes) {
    if (tsActual !== null && ev.ts !== tsActual) {
      // Cierra el instante anterior con el estado acumulado hasta él.
      instantes.push(snapshot(tsActual));
    }
    tsActual = ev.ts;
    const key = entityKeyPlayback(ev.entidad_ref);
    const previo = acumulado.get(key) ?? { entidad_ref: ev.entidad_ref, estado: {} };
    acumulado.set(key, {
      entidad_ref: ev.entidad_ref,
      estado: { ...previo.estado, ...ev.payload },
    });
  }
  if (tsActual !== null) {
    instantes.push(snapshot(tsActual));
  }

  return instantes;
}

/** Acota un índice al rango válido `[0, longitud - 1]` (0 si está vacío). */
function clampIndice(indice: number, longitud: number): number {
  if (longitud <= 0) return 0;
  return clamp(Math.trunc(indice), 0, longitud - 1);
}

/**
 * Reductor **puro** de control del Playback_Temporal: dado el estado
 * vigente y un {@link ComandoPlayback}, devuelve el **nuevo** estado sin
 * mutar la entrada (R2.2).
 *
 * Invariante (Property 6): para cualquier estado válido y cualquier
 * secuencia de comandos (`play`, `pause`, `next`, `prev`, `seek`), el
 * `indice` resultante permanece dentro de `[0, instantes.length - 1]`
 * (o 0 si no hay instantes), por lo que el instante vigente nunca excede
 * el instante inicial ni el final del rango.
 *
 * @param estado  Estado vigente del Playback_Temporal (no se muta).
 * @param comando Comando de control a aplicar.
 */
export function avanzarInstante(estado: EstadoPlayback, comando: ComandoPlayback): EstadoPlayback {
  const longitud = estado.instantes.length;
  switch (comando.tipo) {
    case "play":
      return { ...estado, reproduciendo: true };
    case "pause":
      return { ...estado, reproduciendo: false };
    case "next":
      return { ...estado, indice: clampIndice(estado.indice + 1, longitud) };
    case "prev":
      return { ...estado, indice: clampIndice(estado.indice - 1, longitud) };
    case "seek":
      return { ...estado, indice: clampIndice(comando.indice, longitud) };
    default:
      return estado;
  }
}

/** Devuelve el {@link InstantePlayback} vigente, o `null` si no hay instantes. */
export function instanteVigente(estado: EstadoPlayback): InstantePlayback | null {
  if (estado.instantes.length === 0) return null;
  const idx = clampIndice(estado.indice, estado.instantes.length);
  return estado.instantes[idx];
}

/**
 * Construye el {@link EstadoPlayback} inicial a partir de un log de
 * eventos y un rango temporal, exigiendo que el rango sea válido
 * (`inicio < fin`, R2.7). Función **pura**: misma entrada → mismo estado.
 *
 * @returns `Result` con el estado inicial (índice 0, en pausa) o un error
 *          explícito si el rango es inválido (R2.7).
 */
export function playback(
  eventos: readonly EventoOperacional[],
  rango: RangoTemporal,
): Result<EstadoPlayback> {
  if (!rangoValido(rango)) {
    return {
      ok: false,
      error:
        "Rango temporal inválido: el instante inicial debe ser estrictamente anterior al final (R2.7).",
    };
  }
  return {
    ok: true,
    value: { instantes: instantesEnRango(eventos, rango), indice: 0, reproduciendo: false },
  };
}

/**
 * PlaybackController: controlador con estado del Playback_Temporal del
 * Mapa_Operacional. Deriva la secuencia de instantes del Event_Store
 * dentro de un rango válido (R2.1) y expone los controles `play`,
 * `pause`, `next`, `prev` y `seek`, delegando toda transición en el
 * reductor puro {@link avanzarInstante} para garantizar que el instante
 * vigente nunca exceda los límites del rango (R2.2).
 */
export class PlaybackController {
  private estado: EstadoPlayback;

  private constructor(estadoInicial: EstadoPlayback) {
    this.estado = estadoInicial;
  }

  /**
   * Crea un PlaybackController a partir de un log de eventos ya disponible
   * en memoria. Rechaza con error si el rango es inválido (R2.7).
   */
  static desdeEventos(
    eventos: readonly EventoOperacional[],
    rango: RangoTemporal,
  ): Result<PlaybackController> {
    const init = playback(eventos, rango);
    if (!init.ok) return init;
    return { ok: true, value: new PlaybackController(init.value) };
  }

  /**
   * Crea un PlaybackController derivando los instantes del {@link EventStore}
   * inyectado (R2.1). Lee el log global vía `streamFrom` y rechaza con
   * error si el rango es inválido (R2.7).
   */
  static async desdeEventStore(
    store: EventStore,
    rango: RangoTemporal,
  ): Promise<Result<PlaybackController>> {
    if (!rangoValido(rango)) {
      return {
        ok: false,
        error:
          "Rango temporal inválido: el instante inicial debe ser estrictamente anterior al final (R2.7).",
      };
    }
    const eventos: EventoOperacional[] = [];
    for await (const ev of store.streamFrom(1)) {
      eventos.push(ev);
    }
    return PlaybackController.desdeEventos(eventos, rango);
  }

  /** Inicia la reproducción (no mueve el instante vigente, R2.2). */
  play(): void {
    this.estado = avanzarInstante(this.estado, { tipo: "play" });
  }

  /** Pausa la reproducción (no mueve el instante vigente, R2.2). */
  pause(): void {
    this.estado = avanzarInstante(this.estado, { tipo: "pause" });
  }

  /** Avanza un instante sin exceder el instante final del rango (R2.2). */
  next(): void {
    this.estado = avanzarInstante(this.estado, { tipo: "next" });
  }

  /** Retrocede un instante sin exceder el instante inicial del rango (R2.2). */
  prev(): void {
    this.estado = avanzarInstante(this.estado, { tipo: "prev" });
  }

  /** Salta a un instante por índice, acotado a los límites del rango (R2.2). */
  seek(indice: number): void {
    this.estado = avanzarInstante(this.estado, { tipo: "seek", indice });
  }

  /** Instante vigente reproducido, o `null` si el rango no tiene eventos. */
  instanteVigente(): InstantePlayback | null {
    return instanteVigente(this.estado);
  }

  /** Indica si la reproducción está activa. */
  isReproduciendo(): boolean {
    return this.estado.reproduciendo;
  }

  /** Snapshot inmutable del estado vigente del Playback_Temporal. */
  estadoActual(): EstadoPlayback {
    return this.estado;
  }
}
